import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Small ⓘ icon that toggles an explainer bubble on tap/click. Built for
 * touch-first use (no hover): opens on tap, closes on outside tap or a
 * second tap. The bubble is position:fixed and clamped to the viewport so
 * it never gets cut off inside cards or at screen edges.
 *
 * Keep texts to 1–3 short sentences: say WHAT the number is and HOW it is
 * calculated — the reader is standing in front of a number that surprised
 * them.
 */
export default function InfoHint({
  text,
  label = "Erklärung",
  className = "",
}: {
  text: ReactNode;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const btn = btnRef.current;
      const bubble = bubbleRef.current;
      if (!btn || !bubble) return;
      const b = btn.getBoundingClientRect();
      const w = bubble.offsetWidth;
      const margin = 8;
      let left = b.left + b.width / 2 - w / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));
      let top = b.bottom + 6;
      const h = bubble.offsetHeight;
      if (top + h > window.innerHeight - margin) top = Math.max(margin, b.top - h - 6);
      setPos({ top, left });
    };
    place();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || bubbleRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={
          "inline-grid place-items-center h-4 w-4 rounded-full border text-[10px] leading-none font-semibold align-middle transition shrink-0 " +
          (open
            ? "border-brand-400 text-brand-300 bg-brand-500/15"
            : "border-ink-600 text-ink-400 hover:border-ink-400 hover:text-ink-200") +
          " " +
          className
        }
      >
        i
      </button>
      {open && (
        <div
          ref={bubbleRef}
          role="tooltip"
          className="fixed z-50 w-64 max-w-[85vw] rounded-lg border border-ink-700 bg-ink-900 p-3 text-xs leading-relaxed text-ink-200 shadow-xl"
          style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
        >
          {text}
        </div>
      )}
    </>
  );
}
